import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { Bounce, toast } from "react-toastify";
import { Observable } from "rxjs";
import useAppStore from "@/stores/useAppStore";
import { type AppToken, type ResponseData } from "@/types/app";

// Add API version configuration
const API_VERSION = import.meta.env.VITE_API_VERSION || "v1";

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:7002",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to attach the access token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("app_accessToken");
    if (token && !config.url?.includes("developer.api.autodesk.com")) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to track refreshing process
let isRefreshing = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let refreshSubscribers: any = [];

// Function to subscribe to refresh token requests
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const subscribeTokenRefresh = (cb: any) => {
  refreshSubscribers.push(cb);
};

// Function to trigger all subscribers after token refresh
const onRefreshed = (newAccessToken: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshSubscribers.map((cb: any) => cb(newAccessToken));
  refreshSubscribers = [];
};
const expectionAPI = ["/api/v1/auth/login", "/api/v1/auth/signup"];
// Axios response interceptor to handle 401 Unauthorized (expired token)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (expectionAPI.includes(originalRequest.url)) {
        return Promise.reject(error);
      }
      // Prevent retry loop
      originalRequest._retry = true;

      // If another refresh token request is in progress, wait for it to complete
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshToken = localStorage.getItem("app_refreshToken");
          if (!refreshToken) {
            throw new Error();
          }
          const response = await axiosInstance.post("/api/auth/refresh-token", {
            refreshToken,
          });
          const newAccessToken = (response.data as ResponseData<AppToken>).data
            .accessToken;

          // Store new access token and update the original request
          localStorage.setItem("app_accessToken", newAccessToken);
          axiosInstance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;
          onRefreshed(newAccessToken);
          isRefreshing = false;
          return axiosInstance(originalRequest);
        } catch (err) {
          // If refresh token fails, clear storage and redirect to login
          localStorage.removeItem("app_accessToken");
          localStorage.removeItem("app_refreshToken");
          useAppStore.getState().resetAppStore();
          window.location.href = "/login";
          return Promise.reject(err);
        }
      }

      // If refresh token request is in progress, queue the original request until it's done
      return new Promise((resolve) => {
        subscribeTokenRefresh((newAccessToken: string) => {
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

// Modified axiosRequest to handle API versioning
export const axiosRequest = <T>(
  config: AxiosRequestConfig
): Observable<AxiosResponse<T>> => {
  // Handle API versioning in URLs that start with /api/
  if (config.url && config.url.startsWith("/api/")) {
    // Replace any existing version pattern (/api/v1/, /api/v2/) with the configured version
    config.url = config.url.replace(/\/api\/v\d+\//, `/api/${API_VERSION}/`);

    // If no version pattern found, insert the version after /api/
    if (!config.url.match(/\/api\/v\d+\//)) {
      config.url = config.url.replace("/api/", `/api/${API_VERSION}/`);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new Observable<AxiosResponse<T>>((subscriber: any) => {
    axiosInstance(config)
      .then((response) => {
        subscriber.next(response);
        subscriber.complete();
      })
      .catch((error) => {
        let message = "";
        if (error instanceof AxiosError) {
          message = error.response?.data.error;
          subscriber.error(error.response?.data);
        } else {
          message = error.message;
          subscriber.error(error);
        }
        toast.error(message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          transition: Bounce,
        });
      });
  });
};
